// The two instructions the client builds without a leaf signature: Initialize
// (creates the vault; the payer's own transaction signature is its
// authorization) and Close (mirrors program/src/instructions/close.rs's
// account and data layout around a signature the signer already produced).
// Kept out of digest.ts, which stays a pure function of header/digest bytes,
// and mirrors sdk/rust/src/lib.rs's `ix::initialize` / `ix::close`.

import { concat } from './derive.js';
import { type AccountMetaSpec, closeInstructionData } from './digest.js';
import type { Instruction } from './v1.js';

/** 32 zero bytes: the System program id. */
export const SYSTEM_PROGRAM_ID: Uint8Array = new Uint8Array(32);

/** data = 0x00 || bump || genesis_key (41 bytes); accounts: payer (signer, writable), vault (writable), system program (readonly). */
export function initializeInstruction(programId: Uint8Array, payer: Uint8Array, vault: Uint8Array, bump: number, genesisKey41: Uint8Array): Instruction {
  if (genesisKey41.length !== 41) throw new RangeError(`genesis key must be 41 bytes, got ${genesisKey41.length}`);
  if (!Number.isInteger(bump) || bump < 0 || bump > 0xff) throw new RangeError(`bump out of range: ${bump}`);
  const accounts: AccountMetaSpec[] = [
    { pubkey: payer, isSigner: true, isWritable: true },
    { pubkey: vault, isSigner: false, isWritable: true },
    { pubkey: SYSTEM_PROGRAM_ID, isSigner: false, isWritable: false },
  ];
  return { programId, accounts, data: concat(Uint8Array.of(0, bump), genesisKey41) };
}

/** accounts: vault (writable), destination (writable); data = closeInstructionData(signature, validUntilSlot). */
export function closeInstruction(programId: Uint8Array, vault: Uint8Array, destination: Uint8Array, signature: Uint8Array, validUntilSlot: bigint): Instruction {
  const accounts: AccountMetaSpec[] = [
    { pubkey: vault, isSigner: false, isWritable: true },
    { pubkey: destination, isSigner: false, isWritable: true },
  ];
  return { programId, accounts, data: closeInstructionData(signature, validUntilSlot) };
}
